import { ViewportRuler } from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IIKOTableGridComponent } from '../iiko-table-grid/iiko-table-grid.component';
import { filter, first, Observable, Subscription, tap } from 'rxjs';
import { DEFAULT_BOTTOM_MARGIN, DEFAULT_THROTTLE_TIME } from '../model/constants';

@Directive({
  selector: '[restoTableGridStretchByHeight]',
})
export class StretchByHeightDirective implements OnInit {
  /**  Time in milliseconds to throttle table height calculation whenever the size of the viewport changes  */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('restoThrottleTime') throttleTime = DEFAULT_THROTTLE_TIME;

  /** Margin in pixels between the bottom of the viewport and the table grid container */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('restoBottomMargin') bottomMargin = DEFAULT_BOTTOM_MARGIN;

  /** An element relative to which we stretch the table  */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('restoAnchorElement') relativeAnchorElement: HTMLElement | undefined = undefined;

  /** An external observable to trigger refresh event  */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('restoRefreshObservable') set refreshHeightObserver(value: Observable<void>) {
    if (this._refreshHeightObserverSubscription) {
      this._refreshHeightObserverSubscription.unsubscribe();
    }

    if (!value) {
      return;
    }

    this._refreshHeightObserverSubscription = value
      .pipe(
        tap(() => this.updateViewportHeight()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  destroyRef = inject(DestroyRef);

  private _refreshHeightObserverSubscription: Subscription | undefined = undefined;

  constructor(
    private tableGridComponent: IIKOTableGridComponent,
    private viewportRuler: ViewportRuler,
    private ngZone: NgZone,
    private changeDetectionRef: ChangeDetectorRef,
    private elRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.tableGridComponent.tabulatorInitialized
      .pipe(
        filter((initialized: boolean) => initialized),
        first(),
        tap(() => this.updateViewportHeight()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.viewportRuler
      .change(this.throttleTime)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.ngZone.run(() => this.updateViewportHeight()));
  }

  refreshHeight(): void {
    this.updateViewportHeight();
  }

  private updateViewportHeight(): void {
    const tableBBox = this.elRef.nativeElement.getBoundingClientRect();

    let anchorElHeight;
    let relativeY;
    if (this.relativeAnchorElement) {
      const anchorElBBox = this.relativeAnchorElement.getBoundingClientRect();
      anchorElHeight = anchorElBBox.height;
      relativeY = tableBBox.y - anchorElBBox.y;
    } else {
      anchorElHeight = this.getViewportHeight();
      relativeY = tableBBox.y;
    }

    const bottomPadding = this.bottomMargin;
    const tableHeight = anchorElHeight - relativeY - bottomPadding;
    this.tableGridComponent.height = `${tableHeight}px`;

    this.changeDetectionRef.detectChanges();
  }

  private getViewportHeight(): number {
    const { height } = this.viewportRuler.getViewportSize();
    return height;
  }
}
